/**
 * 🃏 사진 카드(wq-cards) — 순수 유틸.
 *
 * 서버(main.py)가 모델의 ```wq-cards 펜스를 본문에서 `[[rich:N]]` 자리표시자로 바꾸고,
 * `rich_block` SSE 이벤트(pending → ok | empty)와 complete.rich_blocks 로 N번째 자리의 카드 묶음을 내려 준다.
 */

export const RICH_PLACEHOLDER_RE = /\[\[rich:(\d+)\]\]/g;
export const RICH_CARDS_MAX = 6;

const isHttpsUrl = (value) => typeof value === 'string' && /^https:\/\//i.test(value);
const isHttpUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);
const text = (value, max) => (typeof value === 'string' ? value.trim().slice(0, max) : '');

/** 카드 정규화 — 화면에 쓸 수 있는 것만 남긴다. 사진은 서버가 고른 https 주소만 쓴다. */
export function normalizeCards(cards) {
  if (!Array.isArray(cards)) return [];
  const out = [];
  for (const raw of cards) {
    if (!raw || typeof raw !== 'object') continue;
    const title = text(raw.title, 60);
    if (!title) continue;
    const image = raw.image && typeof raw.image === 'object' && isHttpsUrl(raw.image.image_url)
      ? {
          image_url: raw.image.image_url,
          thumb_url: isHttpsUrl(raw.image.thumb_url) ? raw.image.thumb_url : raw.image.image_url,
          source_url: isHttpUrl(raw.image.source_url) ? raw.image.source_url : '',
          source_domain: text(raw.image.source_domain, 80),
        }
      : null;
    out.push({
      title,
      badge: text(raw.badge, 20),
      desc: text(raw.desc, 120),
      q: text(raw.q, 80),
      ...(image ? { image } : {}),
    });
    if (out.length >= RICH_CARDS_MAX) break;
  }
  return out;
}

/** 스트리밍 중 rich_block 이벤트를 인덱스별로 누적한다(뒤에 온 이벤트가 덮어쓴다). */
export function mergeRichBlock(existing, event) {
  const list = Array.isArray(existing) ? existing : [];
  if (!event || !Number.isInteger(event.index) || event.index < 0) return list;
  const status = ['pending', 'ok'].includes(event.status) ? event.status : 'empty';
  const cards = status === 'empty' ? [] : normalizeCards(event.cards);
  const next = { index: event.index, kind: 'cards', status, cards };
  if (status === 'ok' && cards.length === 0) next.status = 'empty';
  const filtered = list.filter((item) => item && item.index !== event.index);
  filtered.push(next);
  return filtered.sort((a, b) => a.index - b.index);
}

/** complete 시점의 최종본(저장·화면 공용). 서버 complete.rich_blocks 가 있으면 그것을, 없으면 스트리밍 중 ok 만. */
export function finalizeRichBlocks(completeBlocks, streamed) {
  const source = Array.isArray(completeBlocks) ? completeBlocks : (Array.isArray(streamed) ? streamed : []);
  const out = [];
  for (const block of source) {
    if (!block || !Number.isInteger(block.index)) continue;
    if (block.status && block.status !== 'ok') continue;
    const cards = normalizeCards(block.cards);
    if (cards.length === 0) continue;
    out.push({ index: block.index, kind: 'cards', cards });
  }
  return out.sort((a, b) => a.index - b.index);
}

export function findRichBlock(richBlocks, index) {
  if (!Array.isArray(richBlocks)) return null;
  return richBlocks.find((item) => item && item.index === index) || null;
}

/** 카드 → 번호 목록 글 (복사·읽어 주기용) */
export function cardsToText(cards) {
  return normalizeCards(cards)
    .map((card, i) => `${i + 1}. ${card.title}${card.badge ? ` (${card.badge})` : ''}${card.desc ? ` — ${card.desc}` : ''}`)
    .join('\n');
}

/** 본문의 [[rich:N]] 을 카드 번호 목록 글로 바꾼다. 데이터가 없으면 자리표시자만 지운다. */
export function replaceRichPlaceholders(content, richBlocks) {
  const value = typeof content === 'string' ? content : '';
  if (!value.includes('[[rich:')) return value;
  return value
    .replace(new RegExp(RICH_PLACEHOLDER_RE.source, 'g'), (match, digits) => {
      const block = findRichBlock(richBlocks, Number(digits));
      return block ? cardsToText(block.cards) : '';
    })
    .replace(/\n{3,}/g, '\n\n');
}
